import { z } from 'zod';
import {
  listFiltersQueryFragment,
  optionalEmail,
  optionalPhone,
  optionalText,
  optionalUrl,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import {
  businessManagerFilterSchema,
  type AttachmentRef,
  type BusinessManagerRef,
  type RecordRef,
} from './records.js';

export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export const createAccountSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  website: optionalUrl(),
  phone: optionalPhone(),
  email: optionalEmail(),
  businessCategory: optionalText(120),
  /** 15-character Indian GSTIN, stored upper-case. Blank clears it. */
  gstNumber: z
    .string()
    .trim()
    .toUpperCase()
    .refine((value) => value === '' || GSTIN_PATTERN.test(value), 'Enter a valid 15-character GSTIN')
    .transform((value) => (value === '' ? null : value))
    .nullish(),
  description: optionalText(4000),

  /**
   * What this customer is billed in. Omitted, it is the organisation's own.
   *
   * An ISO 4217 code, uppercased — a three-letter column that accepts `usd`
   * and `USD` as different currencies is a column that will hold both.
   */
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .length(3, 'Use a three-letter currency code, like INR or USD')
    .regex(/^[A-Z]{3}$/u, 'Use a three-letter currency code, like INR or USD')
    .optional(),

  billingStreet: optionalText(200),
  billingCity: optionalText(120),
  billingState: optionalText(120),
  billingPostalCode: optionalText(32),
  billingCountry: optionalText(120),
  shippingStreet: optionalText(200),
  shippingCity: optionalText(120),
  shippingState: optionalText(120),
  shippingPostalCode: optionalText(32),
  shippingCountry: optionalText(120),

  /** The markets this record sells into. Carried across when a lead converts. */
  verticals: z.array(z.string().trim().min(1).max(80)).max(20).optional(),

  /** Parent account, for group structures. */
  parentId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateAccountInput = z.infer<typeof createAccountSchema>;

export const updateAccountSchema = toPatchSchema(createAccountSchema);
export type UpdateAccountInput = z.infer<typeof updateAccountSchema>;

export const ACCOUNT_SORT_FIELDS = ['createdAt', 'updatedAt', 'name'] as const;

export const listAccountsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(ACCOUNT_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  businessCategory: z.string().trim().max(120).optional(),
  /** Only top-level accounts, for rendering the hierarchy tree. */
  rootsOnly: z.stringbool().optional(),
  ...listFiltersQueryFragment,
});
export type ListAccountsQuery = z.infer<typeof listAccountsQuerySchema>;

export interface AccountSummary {
  id: string;
  name: string;
  website: string | null;
  phone: string | null;
  email: string | null;
  businessCategory: string | null;
  /** What this customer's quotes and invoices are raised in. */
  currency: string;
  verticals: string[];
  parent: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountDetail extends AccountSummary {
  gstNumber: string | null;
  description: string | null;
  billingStreet: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  billingCountry: string | null;
  shippingStreet: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingPostalCode: string | null;
  shippingCountry: string | null;
  customFields: Record<string, unknown>;
  children: RecordRef[];
  /** The legacy single-PDF slot. Its file is also in `attachments`. */
  pdfAttachment: AttachmentRef | null;
  /** Every file attached to the account, newest first. */
  attachments: AttachmentRef[];
  /** Rollups shown on the record header. */
  stats: {
    contactCount: number;
    openDealCount: number;
    openDealValue: string;
    wonDealValue: string;
  };
}

/** A decimal total, one entry per currency it is actually held in. */
export interface CurrencyTotal {
  currency: string;
  total: string;
}

/**
 * Quotes, Invoices and the Deal pipeline for an Account — and everything
 * reachable through its Contacts — summarised without double-counting a
 * record reachable both ways. Computed on read: see `AccountsService.
 * financialSummary`.
 *
 * Amounts are never summed across currencies — each total is a list with one
 * entry per currency actually in use, which may be a single entry for most
 * accounts and is never silently wrong for one that is billed in more than
 * one.
 */
export interface AccountFinancialSummary {
  quotes: {
    count: number;
    totals: CurrencyTotal[];
    byStatus: { status: string; count: number; totals: CurrencyTotal[] }[];
  };
  invoices: {
    count: number;
    invoiced: CurrencyTotal[];
    /** Payments allocated to invoices in this summary — never an unrelated receipt. */
    paid: CurrencyTotal[];
    /** `invoiced - paid`, per currency. Cancelled invoices are excluded entirely. */
    outstanding: CurrencyTotal[];
    byStatus: { status: string; count: number }[];
  };
  deals: {
    closedWon: CurrencyTotal[];
    openPipeline: CurrencyTotal[];
    /** Each open deal's own amount x its stage probability, summed. */
    expectedRevenue: CurrencyTotal[];
    closedLost: CurrencyTotal[];
  };
}
